import type { GroupHierarchy } from '../api/group-hierarchy';
import { state } from '../state';
import { RESET_DEVICE_ENABLED } from './reset-device-config';
import { loadResetDeviceData } from './reset-device-data';
import type { RdRow } from './reset-device-types';
import { renderRdResults, renderRdSelection, showRdState } from './reset-device-results';
import {
  openRdFlow, showRdForm, showRdReview, submitRdFlow, closeRdFlow, forceCloseRdFlow,
  backToConfirm, retryRdAdd, applySpareToggle, updateRdGroupLabel, isRdFlowLocked,
} from './reset-device-flow';

// ── Module state ──────────────────────────────────────────────────────────
// Exported `let` + explicit setters: ESM live bindings are read-only to
// importers, so results.ts reads these and the page mutates them via setters.

export let rdRows: RdRow[] = [];
export let rdSearch     = '';
export let rdPage       = 0;
export let rdPageSize   = 10;
export let rdSortCol    = 'name';
export let rdSortAsc    = true;
export let rdSelectedId: string | null = null;
export let rdExcludedNonGeotab = 0;

let rdHierarchy: GroupHierarchy | null = null;
let loadInFlight = false;

export function setRdSearch(s: string):        void { rdSearch = s; }
export function setRdPage(p: number):          void { rdPage = p; }
export function setRdPageSize(n: number):      void { rdPageSize = n; }
export function setRdSelectedId(id: string | null): void { rdSelectedId = id; }

function setRdSort(col: string): void {
  if (rdSortCol === col) rdSortAsc = !rdSortAsc;
  else { rdSortCol = col; rdSortAsc = true; }
  rdPage = 0;
}

/** Radio semantics: picking selects, it never toggles off. Accidental deselect
 *  has no value here, and re-render would fight the native checked state. */
function pickRow(id: string): void {
  if (rdSelectedId === id) return;
  setRdSelectedId(id);
  rdRecalculate();
}

// ── Data ──────────────────────────────────────────────────────────────────

export async function refreshResetDevice(): Promise<void> {
  if (loadInFlight) return;
  loadInFlight = true;
  if (!rdRows.length) showRdState('loading', 'Loading devices…');

  // Snapshot the session before awaiting: if the user logs out (or switches
  // database) while this is in flight, the result belongs to a dead session and
  // must not repopulate the new one's list. Mirrors dashboard.ts:71-112.
  const epoch = state.sessionEpoch;

  try {
    const { rows, hierarchy, excludedNonGeotab } = await loadResetDeviceData();
    if (epoch !== state.sessionEpoch) return;

    rdRows              = rows;
    rdHierarchy         = hierarchy;
    rdExcludedNonGeotab = excludedNonGeotab;

    // Drop a stale pick if that device is gone (e.g. it was just reset).
    if (rdSelectedId && !rows.some((r) => r.id === rdSelectedId)) rdSelectedId = null;

    if (!rows.length) {
      showRdState('empty');
    } else {
      renderRdResults();
      showRdState('results');
    }
  } catch (err) {
    if (epoch !== state.sessionEpoch) return;
    if (!rdRows.length) showRdState('error', 'Failed to load devices: ' + (err as Error).message);
  } finally {
    // Only release the guard if this call still owns it — a logout resets the
    // flag already, and clobbering it here could unblock a dead load.
    if (epoch === state.sessionEpoch) loadInFlight = false;
  }
}

/** Re-render from cached rows on search/sort/page change (no re-fetch). */
function rdRecalculate(): void {
  if (!rdRows.length) return;
  renderRdResults();
}

// ── Page init ─────────────────────────────────────────────────────────────

export function initResetDevicePage(): void {
  if (!RESET_DEVICE_ENABLED) return;

  void refreshResetDevice();

  const root = document.getElementById('resetDevicePage') as (HTMLElement & { _wired?: boolean }) | null;
  if (!root || root._wired) return;
  root._wired = true;

  document.getElementById('rdRefreshBtn')?.addEventListener('click', () => { void refreshResetDevice(); });

  const search = document.getElementById('rdSearch') as HTMLInputElement | null;
  search?.addEventListener('input', () => { setRdSearch(search.value); setRdPage(0); rdRecalculate(); });

  const pageSize = document.getElementById('rdPageSize') as HTMLSelectElement | null;
  pageSize?.addEventListener('change', () => {
    setRdPageSize(parseInt(pageSize.value)); setRdPage(0); rdRecalculate();
  });

  document.getElementById('rdPagination')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-page]') as HTMLElement | null;
    if (btn && !btn.hasAttribute('disabled')) { setRdPage(parseInt(btn.dataset.page!)); rdRecalculate(); }
  });

  // Row pick + column sort, both delegated off the table so re-rendering the
  // whole <table> innerHTML never orphans a listener.
  // Sort headers are real buttons, so click covers Enter/Space natively.
  document.getElementById('rdTable')?.addEventListener('click', (e) => {
    const target = e.target as Element;

    const sortBtn = target.closest('[data-sort]') as HTMLElement | null;
    if (sortBtn) { setRdSort(sortBtn.dataset.sort!); rdRecalculate(); return; }

    // Radios fire their own change event; don't double-handle their click.
    if ((target as HTMLInputElement).classList?.contains('rd-radio-input')) return;

    const tr = target.closest('[data-pick]') as HTMLElement | null;
    if (tr) pickRow(tr.dataset.pick!);
  });

  // Keyboard selection arrives here: arrow keys move between radios in the group
  // and fire `change` without a click.
  document.getElementById('rdTable')?.addEventListener('change', (e) => {
    const radio = e.target as HTMLInputElement;
    if (radio.classList?.contains('rd-radio-input')) pickRow(radio.value);
  });

  // "Clear search" offered when the selected device is filtered out of view.
  document.getElementById('rdSelectedLabel')?.addEventListener('click', (e) => {
    if ((e.target as Element).id !== 'rdClearSearch') return;
    setRdSearch(''); setRdPage(0);
    if (search) search.value = '';
    rdRecalculate();
  });

  // ── Flow entry ──
  document.getElementById('rdResetBtn')?.addEventListener('click', () => {
    const row = rdRows.find((r) => r.id === rdSelectedId);
    if (!row || !rdHierarchy) return;
    openRdFlow(row, rdHierarchy, () => { void refreshResetDevice(); });
  });

  // ── Modal wiring ──
  const modal = document.getElementById('rdModal');
  document.getElementById('rdModalClose')?.addEventListener('click', closeRdFlow);
  modal?.addEventListener('click', (e) => {
    // `rd-locked` is set while a write is in flight — backdrop dismissal would
    // read as "cancelled" when the operation is actually still running.
    if ((e.target as Element).id === 'rdModal' && !modal.classList.contains('rd-locked')) closeRdFlow();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal?.classList.contains('show') && !isRdFlowLocked()) closeRdFlow();
  });

  document.getElementById('rdConfirmCancel')?.addEventListener('click', closeRdFlow);
  document.getElementById('rdConfirmGo')?.addEventListener('click', showRdForm);

  document.getElementById('rdFormBack')?.addEventListener('click', backToConfirm);
  // The form now leads to the review screen; only review executes the writes.
  document.getElementById('rdFormSubmit')?.addEventListener('click', showRdReview);
  document.getElementById('rdReviewBack')?.addEventListener('click', showRdForm);
  document.getElementById('rdReviewGo')?.addEventListener('click', () => { void submitRdFlow(); });
  document.getElementById('rdFormSpare')?.addEventListener('change', applySpareToggle);
  document.getElementById('rdFormGroupList')?.addEventListener('change', updateRdGroupLabel);

  document.getElementById('rdFormGroupToggle')?.addEventListener('click', (e) => {
    const panel = document.getElementById('rdFormGroupPanel');
    const open = panel?.classList.toggle('hidden') === false;
    (e.currentTarget as HTMLElement).setAttribute('aria-expanded', String(open));
  });

  const groupSearch = document.getElementById('rdFormGroupSearch') as HTMLInputElement | null;
  groupSearch?.addEventListener('input', () => {
    const term = groupSearch.value.toLowerCase().trim();
    document.getElementById('rdFormGroupList')
      ?.querySelectorAll<HTMLElement>('.vselector-item')
      .forEach((row) => {
        row.style.display = !term || (row.textContent ?? '').toLowerCase().includes(term) ? '' : 'none';
      });
  });

  document.getElementById('rdFormGroupNone')?.addEventListener('click', () => {
    document.getElementById('rdFormGroupList')
      ?.querySelectorAll<HTMLInputElement>('input[type=checkbox]')
      .forEach((cb) => { cb.checked = false; });
    updateRdGroupLabel();
  });

  document.getElementById('rdOutcomeDone')?.addEventListener('click', closeRdFlow);
  document.getElementById('rdOutcomeRetry')?.addEventListener('click', retryRdAdd);
}

// ── Logout reset ──────────────────────────────────────────────────────────

export function resetResetDevicePage(): void {
  forceCloseRdFlow();

  rdRows       = [];
  rdSearch     = '';
  rdPage       = 0;
  rdSortCol    = 'name';
  rdSortAsc    = true;
  rdSelectedId = null;
  rdHierarchy  = null;
  loadInFlight = false;
  rdExcludedNonGeotab = 0;

  document.getElementById('rdEmpty')?.classList.remove('hidden');
  document.getElementById('rdResults')?.classList.add('hidden');
  document.getElementById('rdErrBox')?.classList.add('hidden');
  document.getElementById('rdLoading')?.classList.add('hidden');

  const search = document.getElementById('rdSearch') as HTMLInputElement | null;
  if (search) search.value = '';
  renderRdSelection();

  // NOTE: listeners are intentionally left wired — the dashboard DOM is static
  // across logout/login, so re-wiring would stack duplicates. The _wired guard in
  // initResetDevicePage keeps wiring to one pass for the page's lifetime.
}
